import {
  FetchBaseQueryError,
  createApi,
  fetchBaseQuery,
  retry,
} from '@reduxjs/toolkit/query/react';
import i18n from 'i18n';
import {
  MAX_RETRY_REQUEST_ATTEMPTS,
  PUBLIC_ENDPOINTS,
  RETRYABLE_MUTATIONS,
} from './constants';

const shouldRetry = (
  fetchError: FetchBaseQueryError,
  attempt: number,
): boolean => {
  if (attempt >= MAX_RETRY_REQUEST_ATTEMPTS) return false;

  if (
    fetchError.status === 'FETCH_ERROR' ||
    fetchError.status === 'PARSING_ERROR' ||
    fetchError.status === 'TIMEOUT_ERROR' ||
    fetchError.status === 'CUSTOM_ERROR'
  ) {
    return true;
  }

  if (typeof fetchError.status === 'number' && fetchError.status >= 500) {
    return true;
  }

  if (typeof fetchError.status === 'number' && fetchError.status === 429) {
    return true;
  }

  return false;
};

const baseQuery = retry(
  fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_BASE_URL,
    prepareHeaders: (headers, { endpoint }) => {
      headers.set(
        'Accept-Language',
        i18n.resolvedLanguage || i18n.language || 'en',
      );

      if (PUBLIC_ENDPOINTS.has(endpoint)) return headers;

      return headers;
    },
    credentials: 'include',
    timeout: 5000,
  }),
  {
    retryCondition: (error, _args, { attempt, baseQueryApi }) => {
      if (
        baseQueryApi.type === 'mutation' &&
        !RETRYABLE_MUTATIONS.has(baseQueryApi.endpoint)
      ) {
        return false;
      }

      const fetchError = error as FetchBaseQueryError;
      return shouldRetry(fetchError, attempt);
    },
  },
);

export const baseApi = createApi({
  reducerPath: 'baseApi',
  baseQuery,
  tagTypes: ['User', 'Chat', 'Project', 'Lists'],
  endpoints: () => ({}),
});
